import { usePool } from '@/components/layout/AppShell'
import LockCountdown from '@/components/ui/LockCountdown'
import PickRow from '@/components/ui/PickRow'
import PoolRules from '@/components/ui/PoolRules'
import TiebreakerPlaceholder from '@/components/ui/TiebreakerPlaceholder'
import TitleCard from '@/components/ui/TitleCard'
import { useGamesForWeek } from '@/features/games/queries'
import {
  useAcknowledgeRules,
  useCurrentWeek,
  useRulesAcknowledgement,
  useTiebreakerMethod,
} from '@/features/pools/queries'
import { cn } from '@/lib/cn'
import { rulesFingerprint } from '@/lib/poolRules'
import { dayHeading, etDayKey, isLocked, slotTime, weekTitle } from '@/lib/time'
import { useMyPicks, useSetWeekLock, useUpsertPick } from './queries'

type WeekGame = NonNullable<ReturnType<typeof useGamesForWeek>['data']>[number]

/* The schedule reads as a day, then a kickoff slot, then the games in it —
   the same shape as the public schedule page, because that is how everyone
   already talks about an NFL week ("the one o'clocks").

   Single pass, no sorting: fetchGamesForWeek already returns kickoff_at
   ascending with NULLs last, so games belonging to the same day and slot arrive
   consecutively and appending to the last group is enough. Sorting again here
   would be a second source of truth for order.

   Games with no kickoff_at collapse into one trailing "TIME TBD" group. Weeks
   16-18 are unscheduled until late in the season and every one of those rows
   would otherwise claim its own heading. */
function groupByDayAndSlot(games: WeekGame[]) {
  const days: {
    key: string
    label: string
    slots: { key: string; kickoff: string | null; games: WeekGame[] }[]
  }[] = []

  for (const game of games) {
    const dayKey = game.kickoff_at ? etDayKey(game.kickoff_at) : 'tbd'
    const slotKey = game.kickoff_at ?? 'tbd'

    let day = days.at(-1)
    if (!day || day.key !== dayKey) {
      day = {
        key: dayKey,
        label: game.kickoff_at ? dayHeading(game.kickoff_at) : 'Time TBD',
        slots: [],
      }
      days.push(day)
    }

    let slot = day.slots.at(-1)
    if (!slot || slot.key !== slotKey) {
      slot = { key: slotKey, kickoff: game.kickoff_at, games: [] }
      day.slots.push(slot)
    }

    slot.games.push(game)
  }

  return days
}

export default function PicksPage() {
  const pool = usePool()
  const { data: week, isPending: weekPending } = useCurrentWeek(pool.season_id)
  const { data: games, isPending: gamesPending } = useGamesForWeek(week?.id)
  const { data: picks } = useMyPicks(pool.id, week?.id)
  const { data: tiebreaker } = useTiebreakerMethod(pool.tiebreaker_method_id)
  const { data: rulesAck, isSuccess: rulesAckLoaded } = useRulesAcknowledgement(pool.id)
  const acknowledge = useAcknowledgeRules(pool.id)
  const upsert = useUpsertPick()
  const setLock = useSetWeekLock()

  const currentRulesVersion = rulesFingerprint({
    tiePolicy: pool.tie_policy,
    lockEnabled: pool.lock_enabled,
    tiebreakerMethodId: pool.tiebreaker_method_id,
  })

  // Only prompt on a positive answer. While the query is loading, or if it
  // failed, the panel stays collapsed and quiet — nagging on every render
  // because a read broke is its own kind of bug, and picking is never gated on
  // this either way.
  const needsAcknowledgement =
    rulesAckLoaded && rulesAck?.rules_version !== currentRulesVersion

  if (weekPending || !week) return <p className="eyebrow px-6 py-10">Loading the week…</p>

  const title = weekTitle(week.week_number, week.label)

  // Presentation only. The database is the authority on whether a pick lands —
  // see the note in features/picks/api.ts. Dimming a row is an affordance, not
  // a guard, and the write path has no lock check of its own.
  const locked = isLocked(week.picks_lock_at)

  // picked_team stores a team abbreviation; PickRow wants which half is filled.
  const byGameId = new Map((picks ?? []).map((pick) => [pick.game_id, pick]))

  const total = games?.length ?? 0
  const pickedCount = games?.filter((game) => byGameId.has(game.id)).length ?? 0
  const days = groupByDayAndSlot(games ?? [])

  // At most one lock exists per pool/user/week — trg_picks_one_lock_per_week
  // guarantees it, so find() rather than filter() is not an assumption.
  const lockedPick = (picks ?? []).find((pick) => pick.is_lock)

  // "All in" means everything the pool asks for is done, which includes the Lock
  // when the pool uses one. Without that, a member with 16 picks and no Lock
  // would be told they were finished while leaving points on the table.
  const lockOutstanding = pool.lock_enabled && !lockedPick
  const allIn = total > 0 && pickedCount === total && !lockOutstanding

  return (
    <>
      <TitleCard title={title} locksAt={week.picks_lock_at} storageKey={`${pool.id}:${week.id}`} />

      <div className="bg-card border-rule sticky top-0 z-20 border-b px-5 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="font-display text-ink text-team">{title}</h1>
          {/* The bare countdown read as an unexplained number. Label it so
              "32d 06:53:19" is legible as time remaining, not elapsed. */}
          <div className="text-right">
            <span className="eyebrow block">Picks lock in</span>
            <LockCountdown locksAt={week.picks_lock_at} />
          </div>
        </div>

        {/* Persistent progress. Lives in the sticky sub-bar rather than the
            AppShell header because the count needs the week's games and this
            pool's picks; hoisting it would mean fetching both a level up. */}
        <div className="mt-2.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="eyebrow">
              {pickedCount} of {total} picked
            </span>
            {allIn ? (
              <span className="text-mark font-mono text-eyebrow tracking-[0.16em] uppercase">
                All in
              </span>
            ) : (
              pool.lock_enabled && (
                <span
                  className={cn(
                    'font-mono text-eyebrow tracking-[0.16em] uppercase',
                    lockedPick ? 'text-mark' : 'text-ink-dim'
                  )}
                >
                  {lockedPick ? `Lock · ${lockedPick.picked_team}` : 'No lock set'}
                </span>
              )
            )}
          </div>
          {/* field-bright is reserved for progress bars — nothing sits on it. */}
          <div className="bg-rule mt-1.5 h-0.5 w-full overflow-hidden">
            <div
              className="bg-field-bright h-full transition-[width] duration-300"
              style={{ width: total > 0 ? `${(pickedCount / total) * 100}%` : '0%' }}
            />
          </div>
        </div>
      </div>

      <div className="px-5 py-5">
        <PoolRules
          // Remount when the terms change so a previous collapse does not hide
          // rules the member has not seen yet.
          key={currentRulesVersion}
          tiePolicy={pool.tie_policy}
          lockEnabled={pool.lock_enabled}
          tiebreakerName={tiebreaker?.name}
          tiebreakerDescription={tiebreaker?.description}
          needsAcknowledgement={needsAcknowledgement}
          onAcknowledge={() => acknowledge.mutate(currentRulesVersion)}
          isAcknowledging={acknowledge.isPending}
          acknowledgeError={acknowledge.error as Error | null}
        />

        {gamesPending && <p className="eyebrow">Loading games…</p>}

        {!gamesPending && total === 0 && (
          <p className="text-ink text-body">
            No games are loaded for {title.toLowerCase()} yet. The schedule fills in as it firms
            up.
          </p>
        )}

        {upsert.isError && (
          <p role="alert" className="border-flag text-flag mb-4 border-l-2 pl-3 text-body">
            That pick didn't save. {(upsert.error as Error).message}
          </p>
        )}

        {setLock.isError && (
          <p role="alert" className="border-flag text-flag mb-4 border-l-2 pl-3 text-body">
            Your Lock didn't move. {(setLock.error as Error).message} Check which game holds it
            before the week starts.
          </p>
        )}

        {days.map((day) => (
          <section key={day.key} className="mb-6 last:mb-0">
            <h2 className="border-rule text-ink mb-3 border-t pt-3 font-mono text-[0.8125rem] font-semibold tracking-[0.16em] uppercase">
              {day.label}
            </h2>

            {day.slots.map((slot) => (
              <div key={slot.key} className="mb-4 last:mb-0">
                <p className="mb-2 font-mono text-eyebrow tracking-[0.14em] uppercase">
                  <span className="text-ink font-semibold">
                    {slot.kickoff ? slotTime(slot.kickoff) : 'Kickoff TBD'}
                  </span>
                  <span className="text-ink-dim">
                    , {slot.games.length} {slot.games.length === 1 ? 'game' : 'games'}
                  </span>
                </p>

                {slot.games.map((game) => {
                  const pick = byGameId.get(game.id)
                  const picked =
                    pick?.picked_team === game.home_team
                      ? 'home'
                      : pick?.picked_team === game.away_team
                        ? 'away'
                        : null

                  return (
                    <PickRow
                      key={game.id}
                      away={game.away_team}
                      home={game.home_team}
                      picked={picked}
                      isLock={pick?.is_lock}
                      locked={locked}
                      lockEnabled={pool.lock_enabled}
                      lockPending={setLock.isPending && setLock.variables?.gameId === game.id}
                      onToggleLock={() =>
                        setLock.mutate({
                          poolId: pool.id,
                          gameId: game.id,
                          currentLockGameId: lockedPick?.game_id ?? null,
                          enabled: !pick?.is_lock,
                        })
                      }
                      onPick={(side) => {
                        const pickedTeam = side === 'home' ? game.home_team : game.away_team
                        return upsert.mutate({
                          poolId: pool.id,
                          gameId: game.id,
                          pickedTeam,
                          // Switching teams drops the Lock: it was a wager on the
                          // team, not on the fixture, so carrying it over would
                          // double down on a call the member just reversed.
                          // Re-tapping the team already picked leaves it alone.
                          isLock: pick?.picked_team === pickedTeam && (pick?.is_lock ?? false),
                        })
                      }}
                    />
                  )
                })}
              </div>
            ))}
          </section>
        ))}

        {/* Sits after the games because that is where the real input will land:
            the tiebreaker is the last thing a member does, not a header field.
            Rendered only when the pool's method actually asks for a number —
            coin_flip and commissioner_decides have requires_prediction = false
            and would get an empty box promising nothing.

            Note what is NOT here: this is outside every completion signal
            above. pickedCount, total and allIn are computed from games and
            picks alone, and adding an unfillable field to them would keep a
            finished member off "All in" with no way to fix it. */}
        {tiebreaker?.requires_prediction && tiebreaker.prediction_label && (
          <TiebreakerPlaceholder label={tiebreaker.prediction_label} />
        )}
      </div>
    </>
  )
}
